import { Alert, Button, EmptyState, Input, LoadingState, Textarea, useToast } from '@orbind/ui'
import { useState } from 'react'
import { PLUGIN_EVENT_LABEL, PLUGIN_SCOPE_LABEL, escopoEscreve, type PluginScope } from '@orbind/core'
import {
  useCadastrarDesenvolvedor,
  useCriarSandbox,
  usePortalDoDesenvolvedor,
  useRecriarSandbox,
  useSubmeterPlugin,
  type StatusDeDesenvolvedor,
} from '../features/plugin/usePortal.ts'
import './portal-dev.css'

/**
 * Portal do desenvolvedor (SPEC-20260929-2025-plugin-portal-do-desenvolvedor).
 *
 * A casa de quem escreve plugin: a referência de como se faz, o próprio
 * cadastro, os plugins publicados e os sandboxes onde testar.
 *
 * A documentação fica NESTA página, e não num link para fora, por um motivo
 * que a SPEC deixou como invariante: documentação que descreve um manifesto
 * diferente do que o validador aceita custa mais que documentação nenhuma. Os
 * escopos e os eventos abaixo vêm de `@orbind/core` — a MESMA fonte que o
 * validador usa. Não há como divergirem.
 */

const TEXTO_DO_STATUS: Record<StatusDeDesenvolvedor, { rotulo: string; tone: 'info' | 'success' | 'warning' | 'danger' }> = {
  pendente: { rotulo: 'Aguardando aprovação', tone: 'info' },
  aprovado: { rotulo: 'Aprovado', tone: 'success' },
  recusado: { rotulo: 'Recusado', tone: 'warning' },
  suspenso: { rotulo: 'Suspenso', tone: 'danger' },
}

export function PortalDoDesenvolvedorPage() {
  const consulta = usePortalDoDesenvolvedor()
  const cadastrar = useCadastrarDesenvolvedor()
  const criarSandbox = useCriarSandbox()
  const recriar = useRecriarSandbox()
  const submeter = useSubmeterPlugin()
  const toast = useToast()

  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [site, setSite] = useState('')
  const [sobre, setSobre] = useState('')
  const [editando, setEditando] = useState(false)
  const [manifesto, setManifesto] = useState('')

  const dados = consulta.data
  const cadastro = dados?.cadastro ?? null

  const enviar = () => {
    cadastrar.mutate(
      {
        displayName: nome.trim(),
        contactEmail: email.trim(),
        website: site.trim() || null,
        about: sobre.trim() || null,
      },
      {
        onSuccess: () => {
          setEditando(false)
          toast.push({ tone: 'success', message: 'Cadastro enviado. O administrador da instalação vai analisar.' })
        },
        onError: (e) =>
          toast.push({
            tone: 'danger',
            message: e instanceof Error ? e.message : 'Não foi possível enviar o cadastro.',
          }),
      },
    )
  }

  const abrirEdicao = () => {
    setNome(cadastro?.displayName ?? '')
    setEmail(cadastro?.contactEmail ?? '')
    setSite(cadastro?.website ?? '')
    setSobre(cadastro?.about ?? '')
    setEditando(true)
  }

  if (consulta.isPending) return <LoadingState variant="detail" label="Carregando o portal" />

  if (consulta.isError) {
    return (
      <div className="pdv">
        <Alert
          tone="danger"
          title="Não foi possível carregar o portal"
          action={
            <Button size="sm" icon="fas fa-redo" onClick={() => void consulta.refetch()}>
              Tentar de novo
            </Button>
          }
        >
          {consulta.error instanceof Error ? consulta.error.message : 'Tente novamente em instantes.'}
        </Alert>
      </div>
    )
  }

  const status = cadastro ? TEXTO_DO_STATUS[cadastro.status] : null

  return (
    <div className="pdv">
      <header className="pdv__head">
        <h1 className="pdv__title">Portal do desenvolvedor</h1>
        <p className="pdv__sub">
          Plugins do Orbind são serviços web independentes: rodam na sua infraestrutura, se descrevem por um
          manifesto e conversam com o Orbind por JWT assinado. Nada do seu código entra no navegador de quem
          instala fora de um iframe da sua própria origem.
        </p>
      </header>

      {/* ------------------------------------------------------- cadastro -- */}

      <section className="pdv__bloco" aria-label="Meu cadastro">
        <h2 className="pdv__h2">Seu cadastro</h2>

        {cadastro && !editando ? (
          <div className="pdv__card">
            <div className="pdv__card-main">
              <span className="pdv__nome">{cadastro.displayName}</span>
              <span className="pdv__meta">{cadastro.contactEmail}</span>
              {cadastro.website ? <span className="pdv__meta">{cadastro.website}</span> : null}
              {/* status nunca só por cor: a palavra está escrita */}
              <span className={`pdv__status pdv__status--${cadastro.status}`}>
                <i
                  className={
                    cadastro.status === 'aprovado'
                      ? 'fas fa-check-circle'
                      : cadastro.status === 'pendente'
                        ? 'fas fa-clock'
                        : 'fas fa-exclamation-circle'
                  }
                  aria-hidden
                />
                {status?.rotulo}
              </span>
              {cadastro.statusReason ? (
                // o motivo da recusa é o que permite corrigir; escondê-lo
                // obrigaria a pessoa a adivinhar ou a abrir um chamado
                <span className="pdv__motivo">{cadastro.statusReason}</span>
              ) : null}
            </div>
            {cadastro.status === 'suspenso' ? null : (
              <Button icon="fas fa-pen" onClick={abrirEdicao}>
                Editar
              </Button>
            )}
          </div>
        ) : (
          <div className="pdv__form">
            {!cadastro ? (
              <p className="pdv__texto">
                Para publicar um plugin, diga quem você é. O administrador da instalação analisa antes de liberar.
              </p>
            ) : null}
            <label className="pdv__campo">
              <span className="pdv__rotulo">Nome público *</span>
              <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Wynk Tecnologia" />
              <span className="pdv__dica">É o que aparece como fornecedor do plugin para quem instala.</span>
            </label>
            <label className="pdv__campo">
              <span className="pdv__rotulo">E-mail de contato *</span>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" />
              <span className="pdv__dica">Usado pelo administrador quando um plugin seu precisar de atenção.</span>
            </label>
            <label className="pdv__campo">
              <span className="pdv__rotulo">Site</span>
              <Input value={site} onChange={(e) => setSite(e.target.value)} placeholder="https://" inputMode="url" />
            </label>
            <label className="pdv__campo">
              <span className="pdv__rotulo">Sobre</span>
              <Textarea value={sobre} onChange={(e) => setSobre(e.target.value)} rows={3} />
            </label>
            <div className="pdv__acoes">
              {cadastro ? (
                <Button variant="ghost" onClick={() => setEditando(false)}>
                  Cancelar
                </Button>
              ) : null}
              <Button
                variant="primary"
                icon="fas fa-paper-plane"
                disabled={cadastrar.isPending || nome.trim() === '' || email.trim() === ''}
                onClick={enviar}
              >
                {cadastro ? 'Salvar' : 'Enviar cadastro'}
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* -------------------------------------------------- meus plugins -- */}

      <section className="pdv__bloco" aria-label="Meus plugins">
        <h2 className="pdv__h2">Seus plugins</h2>
        {cadastro?.status === 'aprovado' ? (
          <div className="pdv__form" style={{ maxWidth: 640 }}>
            <label className="pdv__campo">
              <span className="pdv__rotulo">Submeter pela URL do manifesto</span>
              <Input
                value={manifesto}
                onChange={(e) => setManifesto(e.target.value)}
                placeholder="https://meu-plugin.exemplo.com/orbind-plugin.json"
                inputMode="url"
              />
              <span className="pdv__dica">
                Passa pela mesma validação do cadastro pelo Console, e entra na fila de revisão. Submeter uma
                versão nova volta para a fila — senão a revisão seria decorativa.
              </span>
            </label>
            <div className="pdv__acoes">
              <Button
                variant="primary"
                icon="fas fa-paper-plane"
                disabled={submeter.isPending || manifesto.trim() === ''}
                onClick={() =>
                  submeter.mutate(manifesto.trim(), {
                    onSuccess: () => {
                      setManifesto('')
                      toast.push({ tone: 'success', message: 'Plugin submetido. Aguarde a revisão.' })
                    },
                    onError: (e) =>
                      toast.push({
                        tone: 'danger',
                        message: e instanceof Error ? e.message : 'Não foi possível submeter.',
                      }),
                  })
                }
              >
                {submeter.isPending ? 'Enviando…' : 'Submeter'}
              </Button>
            </div>
          </div>
        ) : cadastro ? (
          <p className="pdv__dica">Você poderá submeter plugins quando o seu cadastro for aprovado.</p>
        ) : null}

        {(dados?.plugins.length ?? 0) === 0 ? (
          <p className="pdv__texto">Nenhum plugin publicado ainda.</p>
        ) : (
          <ul className="pdv__lista">
            {dados?.plugins.map((p) => (
              <li key={p.id} className="pdv__card">
                <div className="pdv__card-main">
                  <span className="pdv__nome">{p.name['pt-BR'] ?? p.key}</span>
                  <span className="pdv__meta">
                    versão {p.version} · {new URL(p.baseUrl).host}
                  </span>
                  <span className="pdv__meta">
                    {p.instalacoes} {p.instalacoes === 1 ? 'instalação ativa' : 'instalações ativas'}
                  </span>
                </div>
                {p.arquivado ? <span className="pdv__status pdv__status--suspenso">Arquivado</span> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ------------------------------------------------------- sandbox -- */}

      <section className="pdv__bloco" aria-label="Sandboxes">
        <h2 className="pdv__h2">Sandboxes</h2>
        <p className="pdv__texto">
          Um workspace de teste, cheio de dado fictício, para instalar o plugin em construção sem chegar perto da
          base de um cliente. Pode destruir e recriar à vontade — é para isso que existe.
        </p>

        {(dados?.sandboxes.length ?? 0) > 0 ? (
          <ul className="pdv__lista">
            {dados?.sandboxes.map((s) => (
              <li key={s.id} className="pdv__card">
                <div className="pdv__card-main">
                  <span className="pdv__nome">{s.name}</span>
                  <span className="pdv__meta">
                    {s.itens} {s.itens === 1 ? 'Item de Trabalho' : 'Itens de Trabalho'} ·{' '}
                    {s.seededAt ? `semeado em ${new Date(s.seededAt).toLocaleDateString('pt-BR')}` : 'vazio'}
                  </span>
                </div>
                <Button
                  icon="fas fa-rotate"
                  disabled={recriar.isPending}
                  onClick={() =>
                    recriar.mutate(s.id, {
                      onSuccess: () => toast.push({ tone: 'success', message: `${s.name} foi recriado.` }),
                    })
                  }
                >
                  Recriar
                </Button>
              </li>
            ))}
          </ul>
        ) : null}

        {(dados?.sandboxes.length ?? 0) < (dados?.maximoDeSandboxes ?? 0) ? (
          <Button
            icon="fas fa-plus"
            disabled={criarSandbox.isPending}
            onClick={() =>
              criarSandbox.mutate(undefined, {
                onSuccess: () => toast.push({ tone: 'success', message: 'Sandbox criado e semeado.' }),
                onError: (e) =>
                  toast.push({
                    tone: 'danger',
                    message: e instanceof Error ? e.message : 'Não foi possível criar o sandbox.',
                  }),
              })
            }
          >
            Criar sandbox
          </Button>
        ) : (
          <p className="pdv__dica">
            Você chegou ao limite de {dados?.maximoDeSandboxes}. Recriar um é barato — apague um antes de criar outro.
          </p>
        )}
      </section>

      {/* -------------------------------------------------- documentação -- */}

      <section className="pdv__bloco" aria-label="Referência">
        <h2 className="pdv__h2">Referência</h2>

        <h3 className="pdv__h3">O manifesto</h3>
        <p className="pdv__texto">
          Sirva este JSON numa URL pública do seu serviço. O Orbind busca uma vez no cadastro e reconsulta a cada
          versão nova.
        </p>
        <pre className="pdv__code">{MANIFESTO_EXEMPLO}</pre>

        <h3 className="pdv__h3">Três regras que o cadastro recusa</h3>
        <ul className="pdv__regras">
          <li>
            <strong>A `baseUrl` não pode ser a origem do Orbind.</strong> O que isola o seu código é o navegador, pela
            origem — servir o plugin na nossa origem anularia isso.
          </li>
          <li>
            <strong>Todo caminho é relativo.</strong> `/telas/x` sim; `https://...` e `//host/x` não. O segundo parece
            caminho e o navegador lê como URL absoluta.
          </li>
          <li>
            <strong>Em produção, `https` e host público.</strong> Desenvolvendo na sua máquina, exponha por túnel.
          </li>
        </ul>

        <h3 className="pdv__h3">Escopos que você pode pedir</h3>
        <ul className="pdv__escopos">
          {(Object.keys(PLUGIN_SCOPE_LABEL) as PluginScope[]).map((s) => (
            <li key={s} className={escopoEscreve(s) ? 'pdv__escopo pdv__escopo--escreve' : 'pdv__escopo'}>
              <code>{s}</code>
              <span>{PLUGIN_SCOPE_LABEL[s]}</span>
            </li>
          ))}
        </ul>
        <p className="pdv__dica">
          Peça só o que usa: quem instala lê esta lista antes de aceitar, e escopo a mais é motivo de recusa.
        </p>

        <h3 className="pdv__h3">Eventos que você pode assinar</h3>
        <ul className="pdv__escopos">
          {Object.entries(PLUGIN_EVENT_LABEL).map(([e, rotulo]) => (
            <li key={e} className="pdv__escopo">
              <code>{e}</code>
              <span>{rotulo}</span>
            </li>
          ))}
        </ul>

        <h3 className="pdv__h3">Conferir o que chega</h3>
        <p className="pdv__texto">
          Toda chamada nossa carrega um JWT assinado com o segredo da sua instalação. Confira quatro coisas, e
          nenhuma é dispensável: a assinatura, o `exp`, o `aud` (é o seu plugin?) e o <strong>`qsh`</strong> — o hash
          do método e do caminho que estão sendo chamados agora. Sem o `qsh`, um token vazado numa URL de iframe
          serve para disparar qualquer coisa no seu plugin.
        </p>
        <p className="pdv__texto">
          Os eventos chegam <strong>ao menos uma vez</strong>: uma resposta perdida faz a entrega sair de novo. Seu
          plugin precisa ser idempotente.
        </p>
      </section>
    </div>
  )
}

const MANIFESTO_EXEMPLO = `{
  "key": "meu-plugin",
  "name": { "pt-BR": "Meu plugin" },
  "version": "1.0.0",
  "baseUrl": "https://meu-plugin.exemplo.com",
  "vendor": { "name": "Minha Empresa" },
  "lifecycle": {
    "installed": "/lifecycle/installed",
    "uninstalled": "/lifecycle/uninstalled"
  },
  "scopes": ["work-item:read"],
  "extensions": [
    { "type": "rail-screen", "key": "tela", "label": { "pt-BR": "Minha tela" },
      "icon": "fas fa-plug", "url": "/telas/principal" },
    { "type": "work-item-panel", "key": "painel", "label": { "pt-BR": "Meu painel" },
      "url": "/paineis/item" },
    { "type": "card-badge", "key": "selo", "endpoint": "/selos", "ttlSeconds": 300 },
    { "type": "event-listener", "key": "eventos", "url": "/eventos",
      "events": ["work_item.assignee_changed"] }
  ]
}`
